// The usage panel on the company record (module 10 DESIGN "The usage panel"; D205, D206; module 07
// S2, S5, S6, S9; module 15 move 5). It answers TWO questions and computes nothing else: whether the
// client is growing or dying, and whether the licence they pay for is being used. The eighteen-month
// bar charts for watched hours, active users, sessions and event coverage are gone -- they drew the
// same wall of rectangles for a flat client and a collapsing one.
import { ActionButton } from '../parts/index.js';
import type { CompanyUsage } from '../api.js';
import { dayMonthYearTime } from '../day.js';
import { yearOnYear, directionWords, licencePosition, staleWords, type YearOnYear } from './account';
import './usage.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** "September 2026", never "Sep 26", which reads as 26 September (BUGS.md D178). */
function monthWord(period: string): string {
  const d = new Date(`${period}T00:00:00Z`);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
/** The newest month any of the lines compares (BUGS.md D164). */
function latestOf(...ys: Array<YearOnYear | null>): string {
  return ys.reduce((m, y) => (y && y.period > m ? y.period : m), '');
}
/**
 * A line whose month is more than one behind the newest says why (BUGS.md D164): "The source broke in June 2026; May 2026
 * is the last comparable month". Exported for its test.
 */
export function lagWords(period: string, latest: string): string | null {
  const months = (p: string) => { const [y, m] = p.split('-').map(Number); return (y ?? 0) * 12 + (m ?? 1) - 1; };
  if (!latest || months(latest) - months(period) <= 1) return null;
  const next = months(period) + 1;
  const broke = `${MONTHS[next % 12]} ${Math.floor(next / 12)}`;
  return `The source broke in ${broke}; ${monthWord(period)} is the last comparable month`;
}

/** A figure in whole units: hours to the hour, never to the hundredth (BUGS.md D178). */
const whole = (n: number): string => Math.round(n).toLocaleString('en-GB');

/**
 * Two questions and no more: is this client growing or dying, and is the licence being used.
 *
 * What was here before drew eighteen months of bars for watched hours, active users, sessions and
 * event coverage, across two products, whether or not anything had moved -- so a client with a flat
 * year and a client falling off a cliff produced the same wall of rectangles. Mike's word for it was
 * spurious, and a figure that reads the same healthy and broken is not an instrument.
 *
 * Every number below is one the copy already holds. Nothing is rebased, smoothed or annualised, a
 * month either side has to be comparable before it is compared, and where the pair does not exist
 * the panel says there is nothing to compare rather than reaching for a shorter window that would
 * flatter or frighten.
 */
export function UsagePanel({ usage, name, isAdmin, open }: { usage: CompanyUsage; name: string; isAdmin: boolean; open: (path: string) => void }) {
  const trade = usage.oneTrade;
  const users = trade ? yearOnYear(trade.months, (m) => m.activeUsers) : null;
  const hours = trade ? yearOnYear(trade.months, (m) => m.watchedHours) : null;
  const licence = licencePosition(trade?.current ?? null);
  const video = usage.oneVideo ? yearOnYear(usage.oneVideo.months, (m) => m.views) : null;
  return (
    // BUGS.md D177: drawn as the record's other sections are, a head band and a body, with where it comes from said once.
    <section className="sec usage" id="a-usage">
      <div className="sh"><h2>Usage</h2></div>
      <div className="sb">
      {!usage.confirmed ? (
        <div className="usage-unconfirmed">
          <p className="gist">{name} is not linked to a OneTrade or OneVideo account. Cobrain links an account when it is sure whose it is; one it is not sure of waits for a person to confirm it, because a wrong client&rsquo;s figures on this page is the one mistake that would end trust in it.</p>
          {isAdmin ? <ActionButton reading type="button" className="opt lead usage-confirm" onClick={() => open('/rails?tab=snowflake')}>Link it to an account</ActionButton> : <p className="src">An admin links it to an account.</p>}
        </div>
      ) : (
        <>
          <div className="usage-q">
            <h3>Growing or dying</h3>
            {users || hours || video ? (
              <ul className="usage-facts">
                {users ? <Line label="People using it" y={users} unit="" latest={latestOf(users, hours, video)} /> : null}
                {hours ? <Line label="Hours watched" y={hours} unit="" latest={latestOf(users, hours, video)} /> : null}
                {video ? <Line label="Views" y={video} unit="" latest={latestOf(users, hours, video)} /> : null}
              </ul>
            ) : (
              <p className="gist">Nothing to compare yet: this client has no pair of complete months a year apart that the analytics count as comparable.</p>
            )}
          </div>
          <div className="usage-q">
            <h3>Is the licence used</h3>
            {licence ? (
              <p className="gist">
                <b>{licence.used.toLocaleString('en-GB')}</b> of <b>{licence.seats.toLocaleString('en-GB')}</b> seats used in the last thirty days &mdash; <b>{licence.pct}%</b>.{' '}
                {licence.spare > 0 ? `${licence.spare.toLocaleString('en-GB')} paid for and not used.` : 'Every seat is in use.'}
              </p>
            ) : (
              <p className="gist">No seat count is on record for this client, so there is nothing to measure use against.</p>
            )}
          </div>
          {(() => { const w = staleWords('The copy of iGame\u2019s analytics', usage.builtAt, new Date(), dayMonthYearTime); return w ? <p className="stale" role="status">{w}</p> : null; })()}
          <p className="src">Source: iGame&rsquo;s own analytics, as copied each night{usage.builtAt ? `; figures rebuilt ${dayMonthYearTime(new Date(usage.builtAt))}.` : '; nothing has been counted for this client yet, as the night\'s copy has not run since it was linked.'}</p>
        </>
      )}
      </div>
    </section>
  );
}

/** One measure, this month against the same month a year ago, with both figures shown so the percentage can be checked. */
function Line({ label, y, unit, latest }: { label: string; y: YearOnYear; unit: string; latest: string }) {
  const lag = lagWords(y.period, latest);
  return (
    <li className={`usage-line dir-${y.direction}`}>
      <span className="usage-label">{label}</span>
      <span className="usage-move">{directionWords(y)}</span>
      <span className="usage-detail">{whole(y.value)}{unit} in {monthWord(y.period)}, against {whole(y.was)}{unit} in {monthWord(y.then)}{lag ? `. ${lag}` : ''}</span>
    </li>
  );
}

/**
 * The seats sentence, under OneTrade's charts (REQ-2026-10-07-167; Mike by pop-up, 7 Oct, "Move it into OneTrade"), the one
 * figure of the old Usage section no chart drew: "734 of 1,100 seats used in the last thirty days, 67%. 366 paid for and
 * not used." Nothing where no seat count is on record.
 */
export function SeatsLine({ usage }: { usage: CompanyUsage }) {
  const licence = licencePosition(usage.confirmed ? usage.oneTrade?.current ?? null : null);
  if (!licence) return null;
  return (
    <p className="seats-line">
      <b>{licence.used.toLocaleString('en-GB')}</b> of <b>{licence.seats.toLocaleString('en-GB')}</b> seats used in the last thirty days, <b>{licence.pct}%</b>.{' '}
      {licence.spare > 0 ? `${licence.spare.toLocaleString('en-GB')} paid for and not used.` : 'Every seat is in use.'}
    </p>
  );
}
